/* eslint-disable react-hooks/exhaustive-deps */
import { useState, useEffect } from 'react';
import {
  Search,
  Plus,
  Edit2,
  Trash2,
  X,
  AlertCircle,
  ChevronLeft,
  ChevronRight,
  BookOpen
} from 'lucide-react';
import adminService from '../../api/services/adminService';
import { toPriceObject } from '../../api/utils/price';
import { getBookCover } from '../../assets/images/books';

const PHASE_INFO = {
  1: { name: 'Early Foundations', ageRange: '3-6 years' },
  2: { name: 'Strong Habits', ageRange: '7-10 years' },
  3: { name: 'Deeper Understanding', ageRange: '11-14 years' },
  4: { name: 'Real-World Leadership', ageRange: '15-18 years' },
};

const COURSE_THEMES = {
  1: 'Kindness & Compassion',
  2: 'Honesty & Integrity',
  3: 'Patience & Self-Control',
  4: 'Respect & Humility',
  5: 'Generosity & Altruism',
  6: 'Responsibility & Justice',
  7: 'Wisdom & Prudence',
  8: 'Gratitude & Contentment',
  9: 'Perseverance & Resilience',
  10: 'Unity & Cooperation',
};

const BOOK_STATUSES = ['draft', 'coming_soon', 'available', 'out_of_stock', 'discontinued'];
const BOOK_TYPES = ['student', 'teacher', 'workbook', 'guide'];
const PRODUCT_CATEGORIES = ['student_books', 'teacher_guides', 'teaching_materials', 'training', 'student_workbooks'];
const PRODUCT_TYPES = [
  'book', 'teacher_guide', 'lesson_plan', 'worksheet', 'rubric',
  'slides', 'poster', 'answer_key', 'audio', 'video',
  'workbook', 'training_book', 'workshop', 'certification',
];

/** Convert underscore_case enum values to human-readable labels. */
const humanize = (str) => str.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());

const formatLegacyBookPrice = (book) => {
  // Try the prices array first (new format), then fall back to legacy price field
  const priceSource = book.prices?.length ? book.prices[0] : book.price;
  const normalized = toPriceObject(priceSource, book.currency || 'USD');
  return normalized?.retail != null ? `$${normalized.retail.toFixed(2)}` : 'N/A';
};
const normalizeAdminCurrency = (currency) => (currency === 'AED' || currency === 'CAD' ? 'USD' : (currency || 'USD'));
const FORMATS = ['physical', 'digital', 'both'];
const CURRENCIES = ['USD'];

const EMPTY_PRICE = { currency: 'USD', retailPrice: '', educatorPrice: '', schoolPrice: '' };

const BooksPage = () => {
  const [books, setBooks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [phaseFilter, setPhaseFilter] = useState('');
  const [courseFilter, setCourseFilter] = useState('');
  const [pagination, setPagination] = useState({ page: 1, pages: 1, total: 0 });
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingBook, setEditingBook] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    shortDescription: '',
    phase: 1,
    course: 1,
    bookType: 'student',
    status: 'coming_soon',
    productCategory: '',
    productType: 'book',
    format: 'physical',
    binding: '',
    educatorOnly: false,
    featured: false,
    prices: [{ ...EMPTY_PRICE }],
    coverImage: '',
    pageCount: '',
    isbn: '',
    language: 'English',
    dimensions: { length: '', width: '', height: '' },
  });
  const [formError, setFormError] = useState('');
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [coverFile, setCoverFile] = useState(null);
  const [coverPreview, setCoverPreview] = useState('');

  // Search is submitted manually; avoid refetching on each keystroke.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    fetchBooks();
  }, [pagination.page, statusFilter, phaseFilter, courseFilter]);

  const fetchBooks = async () => {
    try {
      setLoading(true);
      const params = {
        page: pagination.page,
        limit: 10,
        search: searchTerm || undefined,
        status: statusFilter || undefined,
        phase: phaseFilter || undefined,
        course: courseFilter || undefined
      };
      const response = await adminService.getBooks(params);
      setBooks(response.data);
      setPagination(response.pagination);
    } catch (err) {
      setError('Failed to load books');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setPagination(prev => ({ ...prev, page: 1 }));
    fetchBooks();
  };

  const openCreateModal = () => {
    setEditingBook(null);
    setFormData({
      title: '',
      description: '',
      shortDescription: '',
      phase: 1,
      course: 1,
      bookType: 'student',
      status: 'coming_soon',
      productCategory: '',
      productType: 'book',
      format: 'physical',
      binding: '',
      educatorOnly: false,
      featured: false,
      prices: [{ ...EMPTY_PRICE }],
      coverImage: '',
      pageCount: '',
      isbn: '',
      language: 'English',
      dimensions: { length: '', width: '', height: '' },
    });
    setCoverFile(null);
    setCoverPreview('');
    setFormError('');
    setIsModalOpen(true);
  };

  const openEditModal = async (book) => {
    try {
      const response = await adminService.getBook(book._id);
      const bookData = response.data;
      setEditingBook(bookData);
      setFormData({
        title: bookData.title || '',
        description: bookData.description || '',
        shortDescription: bookData.shortDescription || '',
        phase: bookData.phase || 1,
        course: bookData.course || 1,
        bookType: bookData.bookType || 'student',
        status: bookData.status || 'coming_soon',
        productCategory: bookData.productCategory || '',
        productType: bookData.productType || 'book',
        format: bookData.format || 'physical',
        binding: bookData.binding || '',
        educatorOnly: bookData.educatorOnly || false,
        featured: bookData.featured || false,
        prices: bookData.prices?.length
          ? bookData.prices.map(p => ({ currency: normalizeAdminCurrency(p.currency), retailPrice: p.retailPrice ?? '', educatorPrice: p.educatorPrice ?? '', schoolPrice: p.schoolPrice ?? '' }))
          : [{ ...EMPTY_PRICE }],
        coverImage: bookData.coverImage || '',
        pageCount: bookData.pageCount || '',
        isbn: bookData.isbn || '',
        language: bookData.language || 'English',
        dimensions: {
          length: bookData.dimensions?.length ?? '',
          width: bookData.dimensions?.width ?? '',
          height: bookData.dimensions?.height ?? '',
        },
      });
      setFormError('');
      setCoverFile(null);
      setCoverPreview(getBookCover(bookData.coverImage) || '');
      setIsModalOpen(true);
    } catch (err) {
      setError('Failed to load book details');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError('');

    let coverImageUrl = formData.coverImage;
    if (coverFile) {
      try {
        const uploadRes = await adminService.uploadBookCover(coverFile);
        coverImageUrl = uploadRes.url;
      } catch (err) {
        setFormError(err.response?.data?.message || err.message || 'Cover upload failed');
        return;
      }
    }

    const submitData = {
      ...formData,
      coverImage: coverImageUrl,
      binding: formData.binding || undefined,
      pageCount: formData.pageCount ? parseInt(formData.pageCount) : null,
      dimensions: (formData.dimensions?.length || formData.dimensions?.width || formData.dimensions?.height) ? {
        length: formData.dimensions.length !== '' ? parseFloat(formData.dimensions.length) : undefined,
        width: formData.dimensions.width !== '' ? parseFloat(formData.dimensions.width) : undefined,
        height: formData.dimensions.height !== '' ? parseFloat(formData.dimensions.height) : undefined,
      } : undefined,
      prices: formData.prices.map(p => ({
        currency: p.currency,
        retailPrice: p.retailPrice !== '' ? parseFloat(p.retailPrice) : 0,
        educatorPrice: p.educatorPrice !== '' ? parseFloat(p.educatorPrice) : 0,
        schoolPrice: p.schoolPrice !== '' ? parseFloat(p.schoolPrice) : 0,
      })),
    };

    try {
      if (editingBook) {
        await adminService.updateBook(editingBook._id, submitData);
      } else {
        await adminService.createBook(submitData);
        setPagination(prev => ({ ...prev, page: 1 }));
      }
      setIsModalOpen(false);
      fetchBooks();
    } catch (err) {
      setFormError(err.response?.data?.message || 'An error occurred');
    }
  };

  const handleDelete = async (id) => {
    try {
      await adminService.deleteBook(id);
      setDeleteConfirm(null);
      fetchBooks();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to delete book');
    }
  };

  const getStatusBadge = (status) => {
    const colors = {
      available: 'bg-green-100 text-green-700',
      coming_soon: 'bg-blue-100 text-blue-700',
      out_of_stock: 'bg-yellow-100 text-yellow-700',
      discontinued: 'bg-red-100 text-red-700'
    };
    return colors[status] || 'bg-gray-100 text-gray-700';
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Books</h1>
          <p className="text-gray-500 mt-1">Manage book catalog</p>
        </div>
        <button
          onClick={openCreateModal}
          className="inline-flex items-center px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-colors"
        >
          <Plus className="w-5 h-5 mr-2" />
          Add Book
        </button>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4">
        <div className="flex flex-col lg:flex-row gap-4">
          <form onSubmit={handleSearch} className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="text"
                placeholder="Search books..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              />
            </div>
          </form>
          <select
            value={phaseFilter}
            onChange={(e) => {
              setPhaseFilter(e.target.value);
              setPagination(prev => ({ ...prev, page: 1 }));
            }}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
          >
            <option value="">All Phases</option>
            {[1, 2, 3, 4].map(p => (
              <option key={p} value={p}>Phase {p}: {PHASE_INFO[p].name}</option>
            ))}
          </select>
          <select
            value={courseFilter}
            onChange={(e) => {
              setCourseFilter(e.target.value);
              setPagination(prev => ({ ...prev, page: 1 }));
            }}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
          >
            <option value="">All Courses</option>
            {Object.entries(COURSE_THEMES).map(([num, theme]) => (
              <option key={num} value={num}>Course {num}: {theme}</option>
            ))}
          </select>
          <select
            value={statusFilter}
            onChange={(e) => {
              setStatusFilter(e.target.value);
              setPagination(prev => ({ ...prev, page: 1 }));
            }}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
          >
            <option value="">All Status</option>
            {BOOK_STATUSES.map(s => (
              <option key={s} value={s}>{humanize(s)}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Error Display */}
      {error && (
        <div className="bg-red-50 text-red-600 p-4 rounded-lg flex items-center">
          <AlertCircle className="w-5 h-5 mr-2" />
          {error}
          <button onClick={() => setError(null)} className="ml-auto">
            <X className="w-5 h-5" />
          </button>
        </div>
      )}

      {/* Books Grid */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500"></div>
          </div>
        ) : books.length === 0 ? (
          <div className="text-center py-12">
            <BookOpen className="w-12 h-12 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500">No books found</p>
            <button
              onClick={openCreateModal}
              className="mt-4 inline-flex items-center px-4 py-2 text-primary-500 hover:text-primary-600"
            >
              <Plus className="w-5 h-5 mr-2" />
              Add your first book
            </button>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-100">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Book
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Phase/Course
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Type
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Price
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {books.map((book) => (
                    <tr key={book._id} className="hover:bg-gray-50">
                      <td className="px-6 py-4">
                        <div className="flex items-center">
                          {getBookCover(book.coverImage) ? (
                            <img
                              src={getBookCover(book.coverImage)}
                              alt={book.title}
                              className="w-12 h-16 object-cover rounded mr-3"
                            />
                          ) : (
                            <div className="w-12 h-16 bg-gray-100 rounded mr-3 flex items-center justify-center">
                              <BookOpen className="w-6 h-6 text-gray-400" />
                            </div>
                          )}
                          <div>
                            <div className="text-sm font-medium text-gray-900 line-clamp-1">
                              {book.title}
                            </div>
                            <div className="text-xs text-gray-500">
                              {book.shortDescription?.substring(0, 50)}...
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">P{book.phase}C{book.course}</div>
                        <div className="text-xs text-gray-500">{COURSE_THEMES[book.course]}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="text-sm text-gray-500 capitalize">{book.bookType}</span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="text-sm text-gray-900">
                          {formatLegacyBookPrice(book)}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusBadge(book.status)}`}>
                          {humanize(book.status || '')}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex items-center justify-end space-x-2">
                          <button
                            onClick={() => openEditModal(book)}
                            className="p-2 text-gray-500 hover:text-primary-500 hover:bg-primary-50 rounded-lg transition-colors"
                          >
                            <Edit2 className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => setDeleteConfirm(book)}
                            className="p-2 text-gray-500 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {pagination.pages > 1 && (
              <div className="px-6 py-4 border-t border-gray-100 flex items-center justify-between">
                <p className="text-sm text-gray-500">
                  Showing {(pagination.page - 1) * 10 + 1} to{' '}
                  {Math.min(pagination.page * 10, pagination.total)} of {pagination.total} books
                </p>
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
                    disabled={pagination.page === 1}
                    className="p-2 rounded-lg border border-gray-300 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                  >
                    <ChevronLeft className="w-5 h-5" />
                  </button>
                  <span className="px-4 py-2 text-sm text-gray-700">
                    Page {pagination.page} of {pagination.pages}
                  </span>
                  <button
                    onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
                    disabled={pagination.page === pagination.pages}
                    className="p-2 rounded-lg border border-gray-300 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                  >
                    <ChevronRight className="w-5 h-5" />
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Create/Edit Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75" onClick={() => setIsModalOpen(false)} />
            <div className="inline-block w-full max-w-3xl p-6 my-8 overflow-hidden text-left align-middle transition-all transform bg-white shadow-xl rounded-2xl max-h-[90vh] overflow-y-auto">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900">
                  {editingBook ? 'Edit Book' : 'Add New Book'}
                </h3>
                <button
                  onClick={() => setIsModalOpen(false)}
                  className="p-2 text-gray-400 hover:text-gray-500 rounded-lg"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              {formError && (
                <div className="mb-4 p-3 bg-red-50 text-red-600 rounded-lg text-sm flex items-center">
                  <AlertCircle className="w-4 h-4 mr-2" />
                  {formError}
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Title *</label>
                  <input
                    type="text"
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    required
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  />
                </div>

                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Phase *</label>
                    <select
                      value={formData.phase}
                      onChange={(e) => setFormData({ ...formData, phase: parseInt(e.target.value) })}
                      required
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    >
                      {[1, 2, 3, 4].map(p => (
                        <option key={p} value={p}>Phase {p}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Course *</label>
                    <select
                      value={formData.course}
                      onChange={(e) => setFormData({ ...formData, course: parseInt(e.target.value) })}
                      required
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    >
                      {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map(c => (
                        <option key={c} value={c}>Course {c}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
                    <select
                      value={formData.bookType}
                      onChange={(e) => setFormData({ ...formData, bookType: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    >
                      {BOOK_TYPES.map(t => (
                        <option key={t} value={t}>{humanize(t)}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                    <select
                      value={formData.status}
                      onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    >
                      {BOOK_STATUSES.map(s => (
                        <option key={s} value={s}>{humanize(s)}</option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* Ecommerce fields */}
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Product Category</label>
                    <select
                      value={formData.productCategory}
                      onChange={(e) => setFormData({ ...formData, productCategory: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    >
                      <option value="">— None —</option>
                      {PRODUCT_CATEGORIES.map(c => (
                        <option key={c} value={c}>{humanize(c)}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Product Type</label>
                    <select
                      value={formData.productType}
                      onChange={(e) => setFormData({ ...formData, productType: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    >
                      {PRODUCT_TYPES.map(t => (
                        <option key={t} value={t}>{humanize(t)}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Format</label>
                    <select
                      value={formData.format}
                      onChange={(e) => setFormData({ ...formData, format: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    >
                      {FORMATS.map(f => (
                        <option key={f} value={f}>{humanize(f)}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="flex gap-6">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input type="checkbox" checked={formData.educatorOnly} onChange={e => setFormData({ ...formData, educatorOnly: e.target.checked })} className="rounded" />
                    <span className="text-sm text-gray-700">Educator Only</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input type="checkbox" checked={formData.featured} onChange={e => setFormData({ ...formData, featured: e.target.checked })} className="rounded" />
                    <span className="text-sm text-gray-700">Featured</span>
                  </label>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Short Description</label>
                  <input
                    type="text"
                    value={formData.shortDescription}
                    onChange={(e) => setFormData({ ...formData, shortDescription: e.target.value })}
                    maxLength={300}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    rows={4}
                    maxLength={2000}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  />
                </div>

                {/* Multi-currency Pricing */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="text-sm font-medium text-gray-700">Pricing by Currency</label>
                    <button
                      type="button"
                      onClick={() => setFormData(d => ({ ...d, prices: [...d.prices, { ...EMPTY_PRICE }] }))}
                      className="text-xs text-primary-600 hover:text-primary-700 flex items-center gap-1"
                    >
                      + Add Currency
                    </button>
                  </div>
                  <div className="border border-gray-200 rounded-lg overflow-hidden">
                    <table className="w-full text-sm">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="text-left px-3 py-2 text-xs font-medium text-gray-500">Currency</th>
                          <th className="text-left px-3 py-2 text-xs font-medium text-gray-500">Retail</th>
                          <th className="text-left px-3 py-2 text-xs font-medium text-gray-500">Educator</th>
                          <th className="text-left px-3 py-2 text-xs font-medium text-gray-500">Institution</th>
                          <th className="px-2 py-2"></th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {formData.prices.map((price, i) => (
                          <tr key={i}>
                            <td className="px-2 py-2">
                              <select
                                value={price.currency}
                                onChange={e => setFormData(d => ({ ...d, prices: d.prices.map((p, idx) => idx === i ? { ...p, currency: e.target.value } : p) }))}
                                className="w-full border border-gray-200 rounded px-2 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-primary-300"
                              >
                                {CURRENCIES.map(c => <option key={c} value={c}>{c}</option>)}
                              </select>
                            </td>
                            {['retailPrice', 'educatorPrice', 'schoolPrice'].map(field => (
                              <td key={field} className="px-2 py-2">
                                <input
                                  type="number" step="0.01" min="0"
                                  value={price[field]}
                                  onChange={e => setFormData(d => ({ ...d, prices: d.prices.map((p, idx) => idx === i ? { ...p, [field]: e.target.value } : p) }))}
                                  className="w-full border border-gray-200 rounded px-2 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-primary-300"
                                  placeholder="0.00"
                                />
                              </td>
                            ))}
                            <td className="px-2 py-2">
                              {formData.prices.length > 1 && (
                                <button type="button" onClick={() => setFormData(d => ({ ...d, prices: d.prices.filter((_, idx) => idx !== i) }))} className="text-red-400 hover:text-red-600">✕</button>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Pages</label>
                    <input
                      type="number"
                      value={formData.pageCount}
                      onChange={(e) => setFormData({ ...formData, pageCount: e.target.value })}
                      min="1"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">ISBN</label>
                    <input
                      type="text"
                      value={formData.isbn}
                      onChange={(e) => setFormData({ ...formData, isbn: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Binding</label>
                    <select
                      value={formData.binding}
                      onChange={(e) => setFormData({ ...formData, binding: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    >
                      <option value="">— Not specified —</option>
                      <option value="hardcover">Hardcover</option>
                      <option value="softcover">Softcover</option>
                      <option value="both">Both available</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Dimensions (cm) L × W × H</label>
                    <div className="grid grid-cols-3 gap-1">
                      {['length', 'width', 'height'].map(dim => (
                        <input
                          key={dim}
                          type="number"
                          step="0.1"
                          min="0"
                          placeholder={dim.charAt(0).toUpperCase()}
                          value={formData.dimensions?.[dim] ?? ''}
                          onChange={(e) => setFormData(d => ({ ...d, dimensions: { ...d.dimensions, [dim]: e.target.value } }))}
                          className="w-full px-2 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 text-sm"
                        />
                      ))}
                    </div>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Cover Image</label>
                  {(coverPreview || getBookCover(formData.coverImage)) && (
                    <div className="flex items-start gap-3 mb-2">
                      <img
                        src={coverPreview || getBookCover(formData.coverImage)}
                        alt="Cover preview"
                        className="w-20 h-28 object-cover rounded border border-gray-200"
                      />
                      <button
                        type="button"
                        onClick={() => {
                          setCoverFile(null);
                          setCoverPreview('');
                          setFormData(prev => ({ ...prev, coverImage: null }));
                        }}
                        className="text-xs text-red-500 hover:text-red-700 underline mt-1"
                      >
                        Remove cover
                      </button>
                    </div>
                  )}
                  <input
                    type="file"
                    accept="image/jpeg,image/png,image/webp"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) {
                        setCoverFile(file);
                        setCoverPreview(URL.createObjectURL(file));
                      }
                    }}
                    className="w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-medium file:bg-primary-50 file:text-primary-600 hover:file:bg-primary-100"
                  />
                </div>

                <div className="flex justify-end space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={() => setIsModalOpen(false)}
                    className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-colors"
                  >
                    {editingBook ? 'Save Changes' : 'Add Book'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteConfirm && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75" onClick={() => setDeleteConfirm(null)} />
            <div className="inline-block w-full max-w-sm p-6 my-8 overflow-hidden text-left align-middle transition-all transform bg-white shadow-xl rounded-2xl">
              <div className="flex items-center justify-center w-12 h-12 mx-auto bg-red-100 rounded-full mb-4">
                <Trash2 className="w-6 h-6 text-red-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 text-center mb-2">Delete Book</h3>
              <p className="text-gray-500 text-center mb-6">
                Are you sure you want to delete "<strong>{deleteConfirm.title}</strong>"? This action cannot be undone.
              </p>
              <div className="flex justify-center space-x-3">
                <button
                  onClick={() => setDeleteConfirm(null)}
                  className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={() => handleDelete(deleteConfirm._id)}
                  className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors"
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BooksPage;
